import { FormHelperText, useTheme } from "@mui/material";
import Select, {
  DropdownIndicatorProps,
  IndicatorSeparatorProps,
  components,
} from "react-select";
import { useEffect, useState } from "react";

import ChatFormLabel from "./ChatFormLabel";
import { UnfoldMoreOutlined } from "@mui/icons-material";
import { useGetModels } from "../../views/models/ModelService";

const IndicatorSeparator = ({ innerProps }: IndicatorSeparatorProps) => {
  return <span {...innerProps} />;
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <components.DropdownIndicator {...props}>
      <UnfoldMoreOutlined />
    </components.DropdownIndicator>
  );
};

type ModelComboSelectProps = {
  label?: string;
  helperText?: string;
  value: any;
  onChange: (selected: any) => void;
  onlyDeployed?: boolean;
  onlyAgentBindable?: boolean;
  isClearable?: boolean;
  productId?: number | null;
  isDisabled?: boolean;
  error?: boolean;
  errorMessage?: string;
};

/** Undefined means the backend predates the flag, so fail open rather than empty the picker. */
export const isAgentBindable = (model: any) => model.agent_bindable !== false;

/**
 * The models an agent-binding picker may offer. keepModelId is never filtered out,
 * so gating a model cannot drop the selection off an agent already bound to it.
 */
export const getEligibleModels = (
  modelList: any[],
  {
    onlyDeployed = false,
    onlyAgentBindable = false,
    keepModelId = null,
  }: {
    onlyDeployed?: boolean;
    onlyAgentBindable?: boolean;
    keepModelId?: number | null;
  } = {},
) => {
  const deployed = (
    onlyDeployed
      ? modelList.map((model: any) => ({
          ...model,
          versions: model.versions.filter(
            (version: any) => version.is_deployed === true,
          ),
        }))
      : modelList
  ).filter((model: any) => model.versions.length > 0);

  return onlyAgentBindable
    ? deployed.filter(
        (model: any) => isAgentBindable(model) || model.id === keepModelId,
      )
    : deployed;
};

export const ModelComboSelect = ({
  label,
  helperText,
  value,
  onChange,
  onlyDeployed = false,
  onlyAgentBindable = false,
  isClearable = false,
  productId = null,
  isDisabled = false,
  error = false,
  errorMessage,
}: ModelComboSelectProps) => {
  const [options, setOptions] = useState<any[]>([]);
  const [selectedModel, setSelectedModel] = useState<any | null>(null);
  const [emptiedByAgentGate, setEmptiedByAgentGate] = useState(false);
  const theme = useTheme();

  const { isSuccess: isModelListSuccess, data: modelList } =
    useGetModels(productId);

  // Process modelList and set options
  useEffect(() => {
    if (isModelListSuccess && modelList) {
      const ungated = getEligibleModels(modelList, { onlyDeployed });
      const eligible = onlyAgentBindable
        ? ungated.filter(
            (model: any) => isAgentBindable(model) || model.id === value,
          )
        : ungated;
      const opts = eligible.map((model: any) => ({
        value: model,
        label: model.name,
      }));
      setOptions(opts);
      // Only blame the gate when the ungated list would have had options.
      setEmptiedByAgentGate(
        onlyAgentBindable && opts.length === 0 && ungated.length > 0,
      );

      setSelectedModel(
        typeof value === "number"
          ? (opts.find((option) => option.value.id === value) ?? null)
          : null,
      );
    } else {
      setOptions([]);
      setEmptiedByAgentGate(false);
      setSelectedModel(null);
    }
    // Note: onChange is intentionally excluded from deps to prevent infinite re-renders
    // since it's typically an inline function that changes every render
  }, [isModelListSuccess, modelList, onlyDeployed, onlyAgentBindable, value]);

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      borderColor: error ? theme.palette.error.dark : provided.borderColor,
      "&:hover": {
        borderColor: error
          ? theme.palette.error.dark
          : provided["&:hover"]?.borderColor,
      },
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: theme.zIndex.modal + 1 }),
    placeholder: (provided: any) => ({
      ...provided,
      color: error ? theme.palette.error.dark : provided.color,
    }),
    menuList: (base: any) => ({
      ...base,
      "::-webkit-scrollbar": {
        width: "8px",
        height: "8px",
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
        borderRadius: "8px",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.grey[400],
        borderRadius: "8px",
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: theme.palette.grey[500],
      },
    }),
  };

  return (
    <div>
      {label && <ChatFormLabel label={label} />}
      {helperText && (
        <FormHelperText sx={{ mb: 1 }}>{helperText}</FormHelperText>
      )}
      <Select
        className="model-combo-select"
        isDisabled={isDisabled}
        isClearable={isClearable}
        options={options}
        isMulti={false}
        value={selectedModel}
        placeholder="Select a model"
        onChange={onChange}
        noOptionsMessage={() =>
          emptiedByAgentGate
            ? "No models are available to agents. An admin can enable one with \u201CAvailable to agents\u201D on the model\u2019s page."
            : "No models found"
        }
        components={{ DropdownIndicator, IndicatorSeparator }}
        menuPortalTarget={document.body}
        styles={customStyles}
      />
      {error && errorMessage && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
    </div>
  );
};
export default ModelComboSelect;
